import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { LoadingComponent, CustomAutocomplete, DealSelectionAutocomplete } from "../../../helpers";
import ImportTableWithMap from "../../ImportTable/ImportTableWithMap";
import { loadItemsButton, itemSelector, ImportFormProps } from "../../shared/ImportHelpers";
import { useImportFormAgrianViewModel } from "./AgrianImportForm.vm";
import "./AgrianImportForm.css";

export function AgrianImportForm(props: ImportFormProps) {
	const vm = useImportFormAgrianViewModel(props);

	return (
		<div className="agrian-import-form">
			<Grid
				container
				spacing={1}
				sx={{ alignItems: "stretch", alignContent: "stretch", maxWidth: "md", margin: "auto", width: "100%" }}
			>
				{/* Internal Branch/Deal Selection */}
				<Grid size={12}>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", md: "row" },
							gap: 2,
							width: "100%",
							pt: 1,
						}}
					>
						{vm.canShowBranchSelector && (
							<CustomAutocomplete
								optionLabelField="name"
								loading={!!vm.loading}
								options={props.branches || []}
								selectedOption={vm.internalSelectedBranch}
								onChange={vm.handleInternalBranchChange}
								optionFormatter={(option) => option.name}
								label="Choose your branch"
								sx={{ flex: 1 }}
							/>
						)}
						<DealSelectionAutocomplete
							deals={props.deals}
							selectedDeals={vm.internalSelectedDeal ? [vm.internalSelectedDeal] : []}
							loading={!!vm.loading}
							setSelectedDeals={(deals) => vm.handleInternalDealChange(deals[0] ?? null)}
							sx={{ flex: 1 }}
						/>
					</Box>
				</Grid>

				{/* Load Organizations Button */}
				{vm.canLoadOrganizations && loadItemsButton(!!vm.loading, vm.getOrganizations, "Load Organizations")}

				{/* Organization Selector */}
				{vm.showOrganizationSelector &&
					itemSelector(
						!!vm.loading,
						vm.organizations,
						vm.selectedOrganizations,
						vm.handleOrganizationChange,
						vm.setSelectedOrganizations,
						(organization) => organization.id,
						(organization) => organization.name,
						"Choose your organization(s)",
					)}

				{/* Load Growers Button */}
				{vm.canLoadGrowers && loadItemsButton(!!vm.loading, vm.getGrowers, "Load Growers")}

				{/* Grower Selector */}
				{vm.showGrowerSelector &&
					itemSelector(
						!!vm.loading,
						vm.growers,
						vm.selectedGrowers,
						vm.handleGrowerChange,
						vm.setSelectedGrowers,
						(grower) => grower.id,
						(grower) => grower.name,
						"Choose your grower(s)",
					)}

				{/* Load Farms & Fields Button */}
				{vm.canLoadFarmsAndFields && loadItemsButton(!!vm.loading, vm.getFarmsAndFields, "Load Farms & Fields")}

				{/* Field Selector */}
				{vm.showFieldSelector &&
					itemSelector(
						!!vm.loading,
						vm.fields,
						vm.selectedFields,
						vm.handleFieldChange,
						vm.setSelectedFields,
						(field) => field.id,
						(field) => `${vm.farms.filter((g) => g.id == field.farm_id).at(0)?.name} - ${field.name}`,
						"Choose your field(s)",
					)}

				{/* Load Jobs Button */}
				{vm.canLoadJobs && loadItemsButton(!!vm.loading, vm.getSamplingEvents, "Load Jobs")}

				{/* Loading Spinner */}
				{vm.showLoadingSpinner && (
					<Box className="loading-spinner-container">
						<LoadingComponent />
					</Box>
				)}
			</Grid>

			{/* ImportTable - only when NOT in modal mode */}
			{vm.showImportTable && (
				<>
					<ImportTableWithMap
						jobs={vm.jobs}
						updateJobs={() => {}}
						deals={props.selectedDeals}
						allDeals={props.deals}
						// oxlint-disable-next-line no-unused-vars
						setLoading={(task: string | boolean) => {
							// Loading is managed internally by ViewModel
						}}
						allOptions={{ Boundary: [], Zones: [], Centroids: [], Cores: [], Unknown: [] }}
						resetForm={vm.resetForm}
						integration="Agrian"
						user={vm.userContext}
						featureStats={vm.featureStats}
						allowMap={true}
						selectedCompany={props.selectedCompany ?? undefined}
						salespersons={props.salespersons ?? []}
						branches={props.branches ?? []}
						lastCropOptions={props.lastCropOptions}
						nextCropOptions={props.nextCropOptions}
					/>
				</>
			)}
		</div>
	);
}
